<template>
  <div class="crcdiv">
    <v-card>
      <!-- No more eligible child rider -->
      <OnexAlert v-if="isLastChild" class="mb-4" :type="Notifications.warning" dismissible="false">
        {{
          $t('L_our_records_show_you', {
            policyNo: currentPolicy.contractId,
            expireDate: expireDateFormatted,
          })
        }}.
      </OnexAlert>
      <!-- END - No more eligible child rider -->
      <v-card-text>
        <v-row>
          <v-col cols="12" class="text-center">
            <v-row justify="center">
              <v-col cols="12" md9 py-4>
                <h3 aria-live="assertive" class="font-weight-regular">
                  {{ $t('L_your_request_for_individual_life') }}
                </h3>
              </v-col>
            </v-row>
            <div class="d-flex align-center justify-space-around text-center justify-center my-4">
              <div>
                <div class="font-weight-bold">
                  {{ $t('L_primary_insured') }}
                </div>
                <div>{{ getPrimaryInsured }}</div>
              </div>
              <div>
                <div class="font-weight-bold">
                  {{ headerTimeLabel }}
                </div>
                <div>{{ getMonthlyBankDraftPremium }}</div>
              </div>
              <div>
                <div class="font-weight-bold">
                  {{ $t('L_coverage_amount') }}
                </div>
                <div>{{ getCoverageAmount }}</div>
              </div>
              <div>
                <div class="font-weight-bold">
                  {{ $t('L_request_number') }}
                </div>
                <div>{{ getRequestNumber }}</div>
              </div>
            </div>
            <v-row justify="center">
              <v-col cols="12" sm8>
                <h4 class="mt-3 font-weight-regular">
                  {{ $t('L_this_policy_has_been_added_to') }}
                </h4>
                <h4 class="font-weight-regular" v-html="$t('L_we_have_also', { email: email })" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions>
        <v-row class="d-flex align-center justify-center py-4 mx-0">
          <v-col cols="12" sm="6" md="4">
            <OnexButton
              id="back_to_dashboard"
              block
              color="primary"
              variant="outlined"
              @click="goBackToDashboard"
            >
              {{ $t('L_back_to_dashboard') }}
            </OnexButton>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <OnexButton
              id="continue_to_next_child"
              block
              v-if="hasCRCPackageForCurrentPolicy"
              color="primary"
              @click="continueWithNextChild"
            >
              {{ $t('L_continue_to_next_child') }}
            </OnexButton>
            <OnexButton
              id="continue_to_next_child"
              block
              v-if="isLastChild && !hasCRCPackageForCurrentPolicy"
              color="primary"
              @click="continueWithAddAnotherChild"
            >
              {{ $t('L_add_another_child') }}
            </OnexButton>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </div>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapActions, mapState, mapStores } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { RouteNames } from '@/router/routeNames';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useChildRiderStore } from '../../store/childrider.store';
import { OnexButton, OnexDialog, Notifications, OnexAlert } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'CRCStepF',
  components: {
    OnexButton,
    OnexDialog,
    OnexAlert,
  },
  data() {
    return {
      Notifications,
    };
  },
  async mounted() {
    try {
      this.setLoading(true);
      await this.pullPackages(); // pull latest from server
    } catch (error) {
      this.globalStore.addError({ error, context: 'childrider' }, { root: true });
    } finally {
      this.setLoading(false);
    }
  },
  methods: {
    ...mapActions(useChildRiderStore, {
      crcCancel: 'crcCancel',
      crcStart: 'crcStart',
      pullPackage: 'pullPackage',
      pullPackages: 'pullPackages',
      setLoading: 'setLoading',
    }),
    ...mapActions(usePolicyStore, {
      startAddAChild: 'startAddAChild',
    }),
    async goBackToDashboard() {
      this.crcCancel();
      await this.pullPackages(); // pull latest crc packages from bff
      await this.$router.push({ name: RouteNames.DASHBOARD });
    },
    async continueWithNextChild() {
      await this.crcStart();
      await this.$router.push({ name: RouteNames.CHILD_RIDER });
    },
    async continueWithAddAnotherChild() {
      await this.startAddAChild();
      await this.$router.push({
        name: RouteNames.POLICY_DETAILS,
        params: { contractId: this.selectedPolicyIndex },
      });
    },
  },
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    ...mapState(usePolicyStore, {
      currentPolicy: 'currentPolicy',
      selectedPolicyIndex: 'selectedPolicyIndex',
    }),
    ...mapState(useCustomerStore, {
      customerProfile: 'customerProfile',
      getCustServNumWDash: 'getCustServNumWDash',
    }),
    ...mapState(useChildRiderStore, {
      hasPackageGivenPolicyId: 'hasPackageGivenPolicyId',
      crcExpireDate: 'crcExpireDate',
      expireDate: 'expireDate',
      expirationDate: 'expirationDate',
      isLastChild: 'lastChild',
      selectedFaceAmount: 'NPSelectedFaceAmount',
      selectedPremiumAmount: 'NPSelectedPremiumAmount',
      newPolicy: 'newPolicy',
      getPrimaryInsured: 'NPFullName',
      selectedCoverageOption: 'NPSelectedOption',
      selectedOptionDetails: 'NPSelectedOptionDetails',
    }),
    expireDateFormatted() {
      return this.expirationDate?.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1');
    },
    email() {
      return this.customerProfile.email;
    },
    hasCRCPackageForCurrentPolicy() {
      return this.hasPackageGivenPolicyId(this.currentPolicy.contractId);
    },
    getMonthlyBankDraftPremium() {
      return `$${this.selectedPremiumAmount}`;
    },
    getCoverageAmount() {
      const faceAmount = this.selectedFaceAmount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `$${faceAmount}`;
    },
    getRequestNumber() {
      return this.newPolicy?.requestNumber ? this.newPolicy.requestNumber : 'N/A';
    },
    headerTimeLabel() {
      let timeLabel;
      if (this.currentPolicy?.billingForm === 'A1') {
        if (this.currentPolicy?.billingMode === '01') {
          timeLabel = this.$t('L_quarterly_premium_amount');
        } else if (this.currentPolicy?.billingMode === '03') {
          timeLabel = this.$t('L_quarterly_premium_amount');
        } else if (this.currentPolicy?.billingMode === '06') {
          timeLabel = this.$t('L_semi_annual_premium_amount');
        } else if (this.currentPolicy?.billingMode === '12') {
          timeLabel = this.$t('L_annual_premium_amount');
        } else {
          timeLabel = this.$t('L_monthly_premium_amount');
        }
      } else if (
        this.currentPolicy?.billingForm === 'C2' ||
        this.currentPolicy?.billingForm === 'D2' ||
        this.currentPolicy?.billingForm === 'H1'
      ) {
        timeLabel = this.$t('L_quarterly_premium_amount');
      } else {
        timeLabel = this.$t('L_monthly_premium_amount');
      }
      return timeLabel;
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.crcdiv {
  min-height: 1000px;
}
</style>
